/**
 * Versioned rule sets. A match or tournament stores the rule-set version (and a full snapshot)
 * it started with; parameters can evolve between matches, never during one.
 *
 * All numbers are initial hypotheses to be tested (see BALANCE_REPORT.md), not a validated balance.
 * Percentages are expressed in basis points (1 % = 100 bp) so every computation stays integral.
 */
export interface DrawRules {
  /** Words with tier ≤ this value are considered "common" for fairness checks. */
  commonMaxTier: 1 | 2;
  /** Minimum common words per language for a single-language configuration. */
  minCommonSingleLanguage: number;
  /** Minimum common words per language when a configuration has several languages. */
  minCommonPerLanguageMulti: number;
  /** Minimum distinct common words (all languages of a configuration together). */
  minCommonTotal: number;
  /** For a target (Duel): distinct common words must be ≥ target × this factor. */
  targetSlackFactor: number;
  /** At least one common word of this length (tiles) per configuration. */
  minLongestCommonLength: number;
  vowelsMin: number;
  vowelsMax: number;
  maxSameVowel: number;
  maxSameConsonant: number;
  maxSpecialTiles: number;
  /** Bounded generation budget before falling back to the pre-validated pool. */
  maxAttempts: number;
}

export interface RuleSet {
  version: string;
  rackSize: number;
  minWordLength: number;
  maxWordLength: number;
  /** Standard round duration. */
  timeLimitMs: number;
  /** Server-side tolerance for in-flight submissions after the deadline (same for everyone). */
  latencyGraceMs: number;
  /** Words to reach in the Tactical Duel. */
  duelTargetWords: number;
  /** Base points of a word of n tiles: pointsPerTileOver2 × (n − 2). */
  pointsPerTileOver2: number;
  contract: {
    specialistBonusBp: number;
    duoBonusBp: number;
    trioBonusBp: number;
    quotaPerLanguage: number;
  };
  sharedDiversity: {
    twoLanguagesBonusBp: number;
    threeLanguagesBonusBp: number;
    quotaPerLanguage: number;
  };
  /** Speed bonus = speedBonusMaxBp × (T − t) / T when the Duel target is reached. */
  speedBonusMaxBp: number;
  draw: DrawRules;
}

export const RULES_R1: RuleSet = Object.freeze({
  version: 'r1',
  rackSize: 9,
  minWordLength: 3,
  maxWordLength: 9,
  timeLimitMs: 120_000,
  latencyGraceMs: 750,
  duelTargetWords: 10,
  pointsPerTileOver2: 10,
  contract: Object.freeze({
    specialistBonusBp: 1000,
    duoBonusBp: 2000,
    trioBonusBp: 3000,
    quotaPerLanguage: 3,
  }),
  sharedDiversity: Object.freeze({
    twoLanguagesBonusBp: 2000,
    threeLanguagesBonusBp: 3000,
    quotaPerLanguage: 3,
  }),
  speedBonusMaxBp: 2000,
  draw: Object.freeze({
    commonMaxTier: 2,
    minCommonSingleLanguage: 20,
    minCommonPerLanguageMulti: 8,
    minCommonTotal: 20,
    targetSlackFactor: 2,
    minLongestCommonLength: 6,
    vowelsMin: 3,
    vowelsMax: 5,
    maxSameVowel: 2,
    maxSameConsonant: 2,
    maxSpecialTiles: 1,
    maxAttempts: 400,
  }),
}) as RuleSet;

export const CURRENT_RULES = RULES_R1;

const RULESETS: Record<string, RuleSet> = { [RULES_R1.version]: RULES_R1 };

/**
 * Registers an additional rule set (simulations and future versions). A version can never be
 * redefined: stored games must always resolve to the exact rules they were played with.
 */
export function registerRuleSet(rules: RuleSet): void {
  const existing = RULESETS[rules.version];
  if (existing && existing !== rules) throw new Error(`Rule set already defined: ${rules.version}`);
  RULESETS[rules.version] = rules;
}

export function getRuleSet(version: string): RuleSet {
  const r = RULESETS[version];
  if (!r) throw new Error(`Unknown rule set: ${version}`);
  return r;
}

/** Base points of a word of `tiles` tiles. */
export function wordPoints(tiles: number, rules: RuleSet = CURRENT_RULES): number {
  if (tiles < rules.minWordLength || tiles > rules.maxWordLength) return 0;
  return rules.pointsPerTileOver2 * (tiles - 2);
}
